import { Component, ViewContainerRef, Type, ChangeDetectorRef, ViewChildren, QueryList, Output, EventEmitter, Input, Injector, OnDestroy, ChangeDetectionStrategy } from '@angular/core';
import { Stepper } from '../../../interfaces/stepper';
import { StepperService } from '../../services/stepper/stepper.service';
import { ConfigurationState, LanguageService, eOrderStatusType, eOrderType, eOrderTypeGroup, Order, OrderManagerService, OrderRepositoryService, OrderUIConfiguration, UiConfigurationRequest, UIConfigurationService, Validation, ValidationProblem, ValidationService } from 'reg-hub-common';
import { STEPPER_GLOBAL_OPTIONS, StepperSelectionEvent } from '@angular/cdk/stepper';
import { MatStepper } from '@angular/material/stepper';
import { SearchStepper } from '../../services/stepper/steps/search-stepper';
import { Step, StepValidationState } from 'projects/reg-hub-client/src/interfaces/step';
import { BehaviorSubject, combineLatest, concat, distinctUntilChanged, first, map, Observable, skip, Subject, take, takeUntil, tap, timeout } from 'rxjs';
import { SteppedComponent } from 'projects/reg-hub-client/src/interfaces/stepped-component';
import { ActivatedRoute } from '@angular/router';

@Component({
  selector: 'app-order-stepper',
  templateUrl: './order-stepper.component.html',
  styleUrls: ['./order-stepper.component.css'],
  providers: [
    {
      provide: STEPPER_GLOBAL_OPTIONS,
      useValue: { displayDefaultIndicatorType: false },
    },
  ],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class OrderStepperComponent implements OnDestroy {
  readonly stepTitleKeys: Record<string, string> = { 'Review': 'orders.steps.review', 'Details': 'orders.steps.details', 'Debtors': 'orders.steps.debtors', 'Collateral': 'orders.steps.collateral', 'Sec. Parties': 'orders.steps.secParties', 'Search Criteria': 'orders.steps.searchCriteria', 'Changes': 'orders.steps.changes', 'Report Criteria': 'orders.steps.reportCriteria', 'Debtor Name': 'orders.steps.debtorName', 'Agent': 'orders.steps.agent', 'Debtor': 'orders.steps.debtor', 'Dealers': 'orders.steps.dealers', 'Secured Party': 'orders.steps.securedParty', 'Assignors': 'orders.steps.assignors', 'Assignees': 'orders.steps.assignees', 'Criteria Validation': 'orders.steps.criteriaValidation', 'Criteria': 'orders.steps.criteria', 'Order Info': 'orders.steps.orderInfo', 'Options': 'orders.steps.options', 'Failed to Fetch Steps': 'orders.steps.failedToFetchSteps' };
  @ViewChildren('appDynamicHost', { read: ViewContainerRef }) stepHosts!: QueryList<ViewContainerRef>;

  order!: Order;

  @Input() orderId!: string;
  @Input() orderTypeGroupID!: eOrderTypeGroup;

  @Output() promptSubmissionConfirmationEvent = new EventEmitter<Order>();
  @Output() promptCancelConfirmationEvent = new EventEmitter<void>();

  saving$ = new BehaviorSubject<boolean>(false);
  onDestroy$ = new Subject<void>();
  hideSubmitButton$ = new BehaviorSubject<boolean>(true);

  stepper: Stepper | null = null;
  selectedIndex = 0;
  animationDuration = '750';
  isHistorical: boolean = false;
  finalStepIsSelected: boolean = false;
  finalStepWording: string = 'common.buttons.submit';
  currentSteppedComponent: SteppedComponent | null = null;
  shouldDisableSubmitButton: boolean = false;
  validations: Validation | null = null;
  private uiConfiguration!: OrderUIConfiguration;

  constructor(
    private stepperService: StepperService,
    private orderRepository: OrderRepositoryService,
    private orderManager: OrderManagerService,
    private uiConfigurationService: UIConfigurationService,
    private cdr: ChangeDetectorRef,
    private validationService: ValidationService,
    private route: ActivatedRoute,
    private configurationState: ConfigurationState,
    private languageService: LanguageService
  ) { }

  ngOnInit(): void {
    // currentValidation$ is app-wide and outlives navigation, so drop the previous
    // order's results before this order's steps start rendering against them
    this.validationService.clearValidation();

    this.orderManager.currentOrder
      .pipe(take(1))
      .subscribe(order => {
        this.order = order;

        // Ensure configuration is set before initializing steps
        this.configurationState.setConfiguration().then(() => {
          this.stepper = this.stepperService.initializeSteps(order);
          this.adjustStepperForBusinessReportState();
          this.isHistorical = (this.order.orderStatusTypeID.toLowerCase() == "historicaldraft");
          this.finalStepIsSelected = this.stepper.steps.length === 1;
          this.finalStepWording = this.isHistorical ? 'common.buttons.save' : 'common.buttons.submit';
          this.configurationState.canSubmitOrderType(order.orderTypeID).then(submitAccess => {
            this.hideSubmitButton$.next(!submitAccess);
          });

          this.uiConfigurationService.getOrderUIConfiguration(this.order)
            .subscribe(config => {
              this.uiConfiguration = config;

              setTimeout(() => {
                // Must wait until the mat-steps have rendered or the stepper rejects the index as out of bounds
                this.selectReviewStepIfRequested();
                this.loadStepContent();
              });
            });
        });
      });

    // Validation messages are localized by the backend, so re-validate the current step when the language changes
    this.languageService.langChanges$
      .pipe(distinctUntilChanged(), skip(1), takeUntil(this.onDestroy$))
      .subscribe(() => this.refreshValidation());

    const validationObservable = this.validationService.currentValidation$
      .pipe(
        takeUntil(this.onDestroy$),
        map(validation => (validation.errors?.length ?? 0) > 0));
    combineLatest([
      this.orderManager.savingParty$,
      this.orderManager.savingAsset$,
      validationObservable,
      this.saving$,
      this.hideSubmitButton$
    ]).pipe(takeUntil(this.onDestroy$)).subscribe(values => {
      if (this.isOnFinalStep() && !values.every(val => val === false)) {
        this.shouldDisableSubmitButton = true;
      } else {
        this.shouldDisableSubmitButton = false;
      }
    });
  }

  ngOnDestroy(): void {
    this.onDestroy$.next();
    this.onDestroy$.complete();
  }

  private selectReviewStepIfRequested() {
    if (this.route.snapshot.queryParamMap.get('review') === 'true' && (this.stepper?.steps.length ?? 0) > 1) {
      // Jump straight to the last step without the slide animation, which clips the button shadows on arrival
      this.animationDuration = '0';
      this.selectedIndex = this.stepper!.steps.length - 1;
      this.finalStepIsSelected = true;
      this.cdr.detectChanges();
      setTimeout(() => {
        this.animationDuration = '750';
        this.cdr.markForCheck();
      });
    }
  }

  loadStepContent() {
    if (this.stepper && this.stepHosts.length >= this.selectedIndex) {
      var step = this.stepper!.steps[this.selectedIndex];

      if (step.content) {
        this.stepHosts.forEach(stepHost => {
          stepHost.clear();
        });

        var stepHost = this.stepHosts.toArray()[this.selectedIndex];
        const componentRef = stepHost.createComponent<unknown>(step.content as Type<unknown>);
        var component = componentRef.instance as any;
        this.currentSteppedComponent = component as SteppedComponent;
        this.currentSteppedComponent.uiConfiguration = this.uiConfiguration;
        this.currentSteppedComponent.triggerUiConfigurationReload
          .pipe(takeUntil(this.onDestroy$))
          .subscribe(values => this.reloadUiConfiguration(this.currentSteppedComponent, values));
        this.currentSteppedComponent.saving$ = this.saving$.asObservable();
        component.criteriaVariations = this.stepper.configuration;
      }
    }
  }

  onStepChange(event: StepperSelectionEvent): void {
    const filter = this.currentSteppedComponent?.getValidationState;
    const selectedIndex = this.selectedIndex;

    this.currentSteppedComponent?.onSaving();

    if (this.currentSteppedComponent?.shouldSaveOrderOnStepChange) {
      this.saveOrder(selectedIndex, filter);
    } else {
      //If we aren't saving the overall order, get the validation state from the component itself.
      //We pass in an empty array for errors as the component would be responsible for tracking any errors internally in this case
      this.stepper!.steps[selectedIndex].validationState = this.currentSteppedComponent?.getValidationState([], this.order) ?? StepValidationState.DefaultState;
    }

    this.selectedIndex = event.selectedIndex;
    this.finalStepIsSelected = (event.selectedIndex == this.stepper!.steps.length - 1);

    if (!this.isOnFinalStep()) {
      this.shouldDisableSubmitButton = false;
    }

    this.loadStepContent();
  }

  /** Saves the order (PUT), which returns the full validation, and updates the given step's validation state. */
  private saveOrder(stepIndex: number, filter: ((errors: ValidationProblem[] | undefined, order: Order) => StepValidationState) | undefined): void {
    this.orderManager.saving$.next(true);
    const order = this.orderManager.getCurrentOrderValueForPut();
    this.saving$.next(true);
    var route = `orders/${order.id}`;
    this.orderRepository.updateOrder(route, order)
      .pipe(
        tap((response: any) => this.orderManager.updateOrder(response.resource as Order)),
        map((response: any) => response.validation as Validation),
        tap(validation => {
          this.validations = validation;
          this.validationService.currentValidation$.next(validation);
        }),
        map(validation => filter ? filter(this.mapPathsToLowerCase(validation.errors), order) : StepValidationState.DefaultState))
      .subscribe(state => {
        this.saving$.next(false);
        this.orderManager.saving$.next(false);
        this.stepper!.steps[stepIndex].validationState = state;
      });
  }

  /** Validation messages are localized by the backend, so fetch them again after a language change. */
  private refreshValidation(): void {
    const component = this.currentSteppedComponent;

    // Steps that validate themselves re-run their own call; others (e.g. review pages) get the full PUT
    if (!component || component.refreshValidation() || !component.shouldSaveOrderOnStepChange || this.saving$.value) {
      return;
    }

    component.onSaving();
    this.saveOrder(this.selectedIndex, component.getValidationState);
  }

  private reloadUiConfiguration(component: SteppedComponent | null, values: UiConfigurationRequest) {
    if (!component) {
      return;
    }

    this.uiConfigurationService.getOrderUIConfigurationByValues(values)
      .subscribe(config => {
        this.uiConfiguration = config;
        component.uiConfiguration = config;

        this.cdr.detectChanges();
      });
  }

  previousStep(orderStepper: MatStepper) {
    orderStepper.previous();
  }

  nextStep(orderStepper: MatStepper) {
    if (this.isOnFinalStep()) {
      this.promptSubmissionConfirmationEvent.emit(this.order);
    }

    orderStepper.next();
  }

  cancelInvalid() {
    if (this.order?.orderStatusTypeID == "Invalid") {
      this.promptCancelConfirmationEvent.emit();
    }
  }

  getStepIcon(step: Step): string {
    if (!step) {
      return '';
    }
    if (step.validationState === StepValidationState.DefaultState) {
      return 'edit';
    }

    const stepLabel = step.validationState === StepValidationState.ValidationSuccess ? 'validated' : 'error';
    return stepLabel;
  }

  private isOnFinalStep(): boolean {
    return this.stepper !== null && this.selectedIndex == this.stepper.steps.length - 1;
  }

  private mapPathsToLowerCase(problems: ValidationProblem[] | undefined): ValidationProblem[] | undefined {
    return problems?.map(problem => {
      return {
        path: problem.path.toLowerCase(),
        message: problem.message,
        userFriendlyMessage: problem.userFriendlyMessage,
        entityID: problem.entityID
      };
    });
  }

  private adjustStepperForBusinessReportState() {
    const originatingOrderIDParam = this.route.snapshot.queryParamMap.get('originatingOrderID');
    if (this.order.orderTypeID == eOrderType.businessReport
      && originatingOrderIDParam != null
      && this.stepper!.steps.length > 1) {
      // Only show review step if coming from a search order
      this.stepper!.steps = [this.stepper!.steps[1]];
    }
  }
}